/**
 * The upload lane's words, and its reading of Storage's refusal (spec
 * 2026-09-24, Part A). Pure: no React, no network.
 */
import { UPLOAD_MAX_BYTES } from "@bash/shared";

const MIB = 1024 * 1024;
const GIB = 1024 * MIB;

/**
 * "1.7 GB" / "612 MB". Megabytes round UP, so a file one byte over the cap
 * never reads as the cap itself.
 */
export function formatUploadSize(bytes: number): string {
  return bytes >= GIB
    ? `${(bytes / GIB).toFixed(1)} GB`
    : `${Math.ceil(bytes / MIB)} MB`;
}

/** The refusal's first line, the file card's error plate name line: "<name> is 1.7 GB". */
export function tooLargeHeadline(filename: string, sizeBytes: number): string {
  return `${filename} is ${formatUploadSize(sizeBytes)}`;
}

/**
 * The refusal's second line: the limit that applied and what to do (final
 * review I2). The upload cap is the wall for every file ("Files can be up to
 * 500 MB"); a tighter one is the wall for a kind of file, the byte path's for
 * anything not video or audio ("Files like this can be up to 50 MB").
 */
export function tooLargeAdvice(limitBytes: number): string {
  const which = limitBytes < UPLOAD_MAX_BYTES ? "Files like this" : "Files";
  return `${which} can be up to ${formatUploadSize(limitBytes)}. Export a smaller copy or share a link.`;
}

/** The refusal in one line (the lane, the typing box's note): the file, its size, the limit, what to do. */
export function tooLargeMessage(
  filename: string,
  sizeBytes: number,
  limitBytes: number
): string {
  return `${tooLargeHeadline(filename, sizeBytes)}. ${tooLargeAdvice(limitBytes)}`;
}

/** A pick past the count a lane holds (the ask card, final review M1). */
export function tooManyFiles(limit: number): string {
  return `You can attach up to ${limit} files.`;
}

/** A size in whole megabytes, never 0: a 300 KB file is "1 MB". */
export function wholeMegabytes(bytes: number): number {
  return Math.max(1, Math.round(bytes / MIB));
}

/**
 * The file plate's line while the bytes go up: "Uploading, 206 of 412 MB".
 * `done` counts on the total's own whole-MB scale, so a finished 412.6 MB
 * upload reads 413 of 413, never 412 of 413.
 */
export function uploadingLine(
  progress: number | undefined,
  sizeBytes: number
): string {
  const total = wholeMegabytes(sizeBytes);
  const done = Math.min(total, Math.floor((progress ?? 0) * total));
  return `Uploading, ${done} of ${total} MB`;
}

/**
 * A Storage refusal's plate line, under the file's own name (the file card):
 * no number, so it never states a limit Storage did not enforce (ruling I1).
 */
export const STORAGE_REFUSED_ADVICE =
  "Too large to upload. Export a smaller copy or share a link.";

/**
 * Storage refused the PUT mid-upload for its size, in one line (the lane's row
 * error, the typing box's note): the plate line run on from the name. No
 * number: Storage's cap is a dashboard setting that may lag our own (ruling I1).
 */
export function storageRefusedMessage(filename: string): string {
  const advice = STORAGE_REFUSED_ADVICE;
  return `${filename} is ${advice.charAt(0).toLowerCase()}${advice.slice(1)}`;
}

/**
 * Did Storage refuse this PUT for its size? Supabase answers HTTP 400 with the
 * 413 INSIDE its JSON body ({"statusCode":"413","error":"Payload too large"}),
 * so the status alone never fired; a plain HTTP 413 counts too. Structured
 * fields only, never the message text.
 */
export function isStorageTooLarge(status: number, body: string): boolean {
  if (status === 413) return true;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return false;
  }
  if (typeof parsed !== "object" || parsed === null) return false;
  const { statusCode, error } = parsed as {
    statusCode?: unknown;
    error?: unknown;
  };
  return String(statusCode) === "413" || error === "Payload too large";
}

/**
 * Why a send is held (spec 2026-09-24, Part B): the file it waits for. One
 * spelling for every held key: the typing box's key (label and title) and the
 * file ask's Send.
 */
export function waitingForUpload(filename: string): string {
  return `Waiting for ${filename} to finish uploading`;
}

/** A progress fraction as the whole-number percent a chip shows (0..100). */
export function uploadPercent(fraction: number): number {
  // The epsilon keeps 0.29 * 100 (28.999…) reading as 29.
  return Math.max(0, Math.min(100, Math.floor(fraction * 100 + 1e-9)));
}
